import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  Search, Lightbulb, BookOpen, Filter, ChevronRight,
  ExternalLink, Tag, CheckCircle, Clock, Zap, X,
  Trash2, Activity, Shield, Car, Box, Map, LayoutDashboard,
  Home, Recycle, Flame, TreePine, ScanLine, AlertCircle,
  Upload, FileText, Loader2, Plus, RefreshCw, Download,
  Link2, Send, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import AttachCostModal from "@/components/AttachCostModal";

const ICON_MAP = {
  Trash2, Activity, Shield, Car, Box, Map, LayoutDashboard,
  Home, Recycle, Flame, TreePine, BookOpen, ScanLine,
};

const ESTADO_CFG = {
  produccion:  { label: "En producción",   cls: "bg-green-100 text-green-700 border-green-200",  dot: "bg-green-500" },
  piloto:      { label: "Piloto activo",    cls: "bg-blue-100 text-blue-700 border-blue-200",    dot: "bg-blue-500"  },
  desarrollo:  { label: "En desarrollo",   cls: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  contratado:  { label: "Contratado",       cls: "bg-purple-100 text-purple-700 border-purple-200", dot: "bg-purple-500" },
  concepto:    { label: "Concepto/PoC",    cls: "bg-gray-100 text-gray-500 border-gray-200",    dot: "bg-gray-400"  },
};

const CAT_COLORS = {
  "RSU & Limpieza":       "#22C55E",
  "Seguridad Laboral":    "#F59E0B",
  "Movilidad & ORA":      "#8B5CF6",
  "Mantenimiento Urbano": "#0EA5E9",
  "Gestión Operacional":  "#7C3AED",
  "Economía Circular":    "#14B8A6",
  "Energía & Biogás":     "#6B7280",
  "Zonas Verdes":         "#16A34A",
};

function ProjectModal({ project, onClose }) {
  const [showAttach, setShowAttach] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [licitaciones, setLicitaciones] = useState([]);
  const [selectedLic, setSelectedLic] = useState("");
  const [attaching, setAttaching] = useState(false);

  if (!project) return null;
  const Icon = ICON_MAP[project.icono] || Lightbulb;
  const estadoCfg = ESTADO_CFG[project.estado] || ESTADO_CFG.concepto;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="project-modal"
      >
        {/* Header */}
        <div className="relative p-5 border-b flex items-start gap-4"
          style={{ background: `linear-gradient(135deg, ${project.color}15, ${project.color}05)` }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: project.color + "20", border: `2px solid ${project.color}40` }}>
            <Icon size={22} style={{ color: project.color }} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900 font-['Outfit']">{project.nombre}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${estadoCfg.cls}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${estadoCfg.dot} mr-1 align-middle`} />
                {estadoCfg.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{project.categoria} · {project.subcategoria}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg ml-2">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Description */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Descripción</h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{project.descripcion_larga || project.descripcion}</p>
          </div>

          {/* Tech stack */}
          {project.tecnologias?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Stack tecnológico</h3>
              <div className="flex flex-wrap gap-1.5">
                {project.tecnologias.map(t => (
                  <span key={t} className="text-xs px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg border border-gray-200">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Applicable services */}
          {project.servicios_aplicables?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Servicios aplicables</h3>
              <div className="flex flex-wrap gap-1.5">
                {project.servicios_aplicables.map(s => (
                  <span key={s} className="text-xs px-2.5 py-1 rounded-lg font-medium"
                    style={{ background: project.color + "15", color: project.color, border: `1px solid ${project.color}30` }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bid advantages */}
          {project.ventajas_licitacion?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Valor en licitaciones</h3>
              <ul className="space-y-1.5">
                {project.ventajas_licitacion.map((v, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle size={13} className="mt-0.5 flex-shrink-0" style={{ color: project.color }} />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Reference municipalities */}
          {project.municipios_referencia?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Municipios de referencia</h3>
              <div className="flex flex-wrap gap-1.5">
                {project.municipios_referencia.map(m => (
                  <span key={m} className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded-full text-gray-600">{m}</span>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {project.documentos?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Documentación disponible</h3>
              <div className="space-y-1.5">
                {project.documentos.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                    <FileText size={13} className="flex-shrink-0 text-gray-400" />
                    <span className="flex-1 text-xs">{d.nombre}</span>
                    <span className="text-[9px] text-gray-400 bg-gray-200 rounded px-1.5 py-0.5">{d.tipo}</span>
                    {d.url || d.link
                      ? <a href={d.url || d.link} target="_blank" rel="noreferrer" className="text-[#007934] hover:underline text-[10px] flex items-center gap-0.5"><ExternalLink size={10} />Abrir</a>
                      : <span className="text-[9px] text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">Disponible internamente</span>
                    }
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action: Use in offer / Attach to licitación */}
          <div className="pt-2 border-t border-gray-50 space-y-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const text = `**Valor Añadido Digital — ${project.nombre}**\n${project.descripcion}\n\nVentajas: ${(project.ventajas_licitacion || []).join("; ")}\nServicios: ${(project.servicios_aplicables || []).join(", ")}`;
                  navigator.clipboard.writeText(text).then(() => toast.success(`"${project.nombre}" copiado — pégalo en la oferta`));
                }}
                className="flex items-center justify-center gap-2 border border-[#007934] text-[#007934] rounded-xl py-2.5 px-3 text-sm font-semibold hover:bg-[#007934]/5 transition-colors"
                data-testid="use-in-offer-btn"
              >
                <CheckCircle size={14} /> Copiar
              </button>
              <button
                onClick={async () => {
                  if (!showAttach) {
                    try {
                      const { data } = await axios.get(`${API}/licitaciones`);
                      const items = Array.isArray(data) ? data : data.items || [];
                      setLicitaciones(items.filter(l => !["ganada","perdida","descartada"].includes(l.estado_crm)));
                    } catch { toast.error("Error cargando licitaciones"); }
                  }
                  setShowAttach(v => !v);
                }}
                className="flex-1 flex items-center justify-center gap-2 bg-[#007934] text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-[#006528] transition-colors"
                data-testid="attach-to-licitacion-btn"
              >
                <Link2 size={14} /> Adjuntar a licitación
              </button>
              <button
                onClick={() => setShowCost(true)}
                className="flex-1 flex items-center justify-center gap-2 bg-amber-500 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-amber-600 transition-colors"
                data-testid="attach-cost-btn"
                title="Añadir como línea CAPEX/OPEX al presupuesto de una licitación"
              >
                <ArrowRight size={14} /> Llevar a coste
              </button>
            </div>

            {/* Licitacion selector */}
            {showAttach && (
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <p className="text-xs text-gray-500 font-medium">Selecciona la licitación activa donde incluir este proyecto como valor añadido digital:</p>
                <select
                  value={selectedLic}
                  onChange={e => setSelectedLic(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-[#007934]"
                  data-testid="licitacion-attach-select"
                >
                  <option value="">— Selecciona una licitación —</option>
                  {licitaciones.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.titulo?.substring(0, 60)}{l.titulo?.length > 60 ? "…" : ""} ({l.municipio})
                    </option>
                  ))}
                </select>
                <button
                  disabled={!selectedLic || attaching}
                  onClick={async () => {
                    setAttaching(true);
                    try {
                      await axios.post(`${API}/licitaciones/${selectedLic}/attach-innovation`, {
                        proyecto_id: project.id,
                        proyecto_nombre: project.nombre,
                        proyecto_descripcion: project.descripcion,
                        ventajas_licitacion: project.ventajas_licitacion || [],
                        servicios_aplicables: project.servicios_aplicables || [],
                        tecnologias: project.tecnologias || [],
                      });
                      const lic = licitaciones.find(l => l.id === selectedLic);
                      toast.success(`"${project.nombre}" adjuntado a "${lic?.titulo?.substring(0,40) || selectedLic}"`);
                      setShowAttach(false);
                      setSelectedLic("");
                    } catch (e) {
                      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
                    } finally { setAttaching(false); }
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-[#007934] text-white rounded-lg py-2 text-xs font-semibold disabled:opacity-50 hover:bg-[#006528] transition-colors"
                  data-testid="confirm-attach-btn"
                >
                  {attaching ? <><Loader2 size={12} className="animate-spin" /> Adjuntando…</> : <><ArrowRight size={12} /> Confirmar adjunto</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <AttachCostModal
        open={showCost}
        onClose={() => setShowCost(false)}
        source={{
          type: "innovation",
          id: project.id,
          nombre: project.nombre,
          defaultConcepto: `${project.nombre} — valor añadido digital`,
          defaultTipo: "opex",
          defaultUnidad: "año",
        }}
      />
    </div>
  );
}

function ProjectCard({ project, onClick }) {
  const Icon = ICON_MAP[project.icono] || Lightbulb;
  const estadoCfg = ESTADO_CFG[project.estado] || ESTADO_CFG.concepto;

  return (
    <div
      className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all cursor-pointer group"
      onClick={() => onClick(project)}
      data-testid={`project-card-${project.id}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: project.color + "15" }}>
            <Icon size={18} style={{ color: project.color }} />
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${estadoCfg.cls}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${estadoCfg.dot} mr-1 align-middle`} />
            {estadoCfg.label}
          </span>
        </div>

        <h3 className="text-sm font-bold text-gray-900 font-['Outfit'] mb-1 leading-tight">{project.nombre}</h3>
        <p className="text-[10px] font-medium mb-2" style={{ color: project.color }}>{project.subcategoria}</p>
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{project.descripcion}</p>

        {project.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {project.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-gray-50 border border-gray-100 rounded text-gray-500">{tag}</span>
            ))}
            {project.tags.length > 3 && <span className="text-[9px] text-gray-300">+{project.tags.length - 3}</span>}
          </div>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-gray-50 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">{project.categoria}</span>
        <ChevronRight size={12} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
    </div>
  );
}

/* ── RAG Upload Modal ────────────────────────────────────────── */
function RAGUploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [proyecto, setProyecto] = useState("");
  const [categoria, setCategoria] = useState("Documento externo");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("proyecto", proyecto || (file.name.includes(".") ? file.name.substring(0, file.name.lastIndexOf(".")) : file.name));
      fd.append("categoria", categoria);
      const { data } = await axios.post(`${API}/innovation/upload-rag`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success(`${data.chunks_created} fragmentos indexados en RAG`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(`Error al subir: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="rag-upload-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <Upload size={18} className="text-[#007934]" />
            <h2 className="text-lg font-bold font-['Outfit']">Añadir documento al RAG</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-500">
            Sube un PDF, DOCX, XLSX o TXT. El texto se indexará automáticamente y estará disponible como contexto en el <strong>chatbot de licitaciones</strong>.
          </p>
          <div
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
              file ? "border-[#007934] bg-[#E8F5E9]" : "border-gray-200 hover:border-[#007934] hover:bg-gray-50"
            }`}
          >
            <input ref={inputRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.csv" className="hidden"
              onChange={e => setFile(e.target.files[0] || null)} />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-[#007934]">
                <FileText size={18} />
                <span className="text-sm font-semibold">{file.name}</span>
                <button onClick={e => { e.stopPropagation(); setFile(null); }} className="ml-2 text-gray-400 hover:text-red-500"><X size={14} /></button>
              </div>
            ) : (
              <>
                <Upload size={24} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">Selecciona el documento</p>
                <p className="text-xs text-gray-400 mt-1">.pdf, .docx, .xlsx, .txt — máx 30 MB</p>
              </>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Nombre del proyecto / fuente (opcional)</label>
            <input value={proyecto} onChange={e => setProyecto(e.target.value)}
              placeholder="ej: Guía API Recogida Madrid"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Categoría</label>
            <select value={categoria} onChange={e => setCategoria(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] bg-white">
              <option>Documento externo</option>
              <option>RSU & Limpieza</option>
              <option>Movilidad & ORA</option>
              <option>Seguridad Laboral</option>
              <option>Gestión Operacional</option>
              <option>Mantenimiento Urbano</option>
              <option>Economía Circular</option>
              <option>Integración técnica</option>
              <option>Pliego licitación</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-rag-upload-btn"
            >
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Indexando...</> : <><Upload size={14} /> Subir e indexar</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KnowledgeSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const doSearch = useCallback(async () => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.post(`${API}/innovation/search`, { q: query, limit: 8 });
      setResults(data);
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  }, [query]);

  useEffect(() => {
    const t = setTimeout(doSearch, 350);
    return () => clearTimeout(t);
  }, [doSearch]);

  return (
    <div className="space-y-4" data-testid="knowledge-search">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Busca por tecnología, servicio o municipio... (ej: GPS, ORA, ENS, RSU, Pando)"
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#007934] focus:ring-2 focus:ring-[#007934]/10"
          data-testid="kb-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
            <X size={14} />
          </button>
        )}
      </div>

      {searching && (
        <div className="text-sm text-gray-400 flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-[#007934]/40 border-t-[#007934] rounded-full animate-spin" />
          Buscando en la base de conocimiento...
        </div>
      )}

      {results.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 font-medium">{results.length} resultados para "<span className="text-[#007934]">{query}</span>"</p>
          {results.map((r, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 hover:border-[#007934]/20 transition-all"
              data-testid={`kb-result-${i}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="text-sm font-semibold text-gray-900">{r.titulo}</h4>
                <span className="text-[10px] bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full text-gray-500 flex-shrink-0">{r.categoria}</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{r.contenido}</p>
              {r.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.tags.map(t => (
                    <span key={t} className="text-[9px] px-1.5 py-0.5 bg-[#007934]/5 text-[#007934] rounded">{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : query && !searching ? (
        <div className="text-center py-8">
          <AlertCircle size={28} className="text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Sin resultados para "{query}"</p>
          <p className="text-xs text-gray-300 mt-1">Prueba con términos como: GPS, IoT, ENS, ArcGIS, RSU, ORA, planta...</p>
        </div>
      ) : !query ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
          {["GPS flota", "IoT wearable", "ENS seguridad", "RSU selectiva", "ArcGIS GIS", "app ciudadana", "economía circular", "Pando rutas"].map(s => (
            <button key={s} onClick={() => setQuery(s)}
              className="text-xs px-3 py-2 bg-gray-50 border border-gray-100 rounded-xl text-gray-500 hover:bg-[#007934]/5 hover:text-[#007934] hover:border-[#007934]/20 transition-all text-left">
              <Search size={9} className="inline mr-1" />{s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function InnovacionDigital() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("portfolio");
  const [filterCat, setFilterCat] = useState("all");
  const [filterEstado, setFilterEstado] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [ragDocs, setRagDocs] = useState([]);

  const loadRagDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/innovation/rag-documents`);
      setRagDocs(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/innovation/projects`),
      axios.get(`${API}/innovation/categories`),
    ]).then(([pRes, cRes]) => {
      setProjects(pRes.data);
      setCategories(cRes.data);
    }).finally(() => setLoading(false));
    loadRagDocs();
  }, [loadRagDocs]);

  const filtered = projects.filter(p => {
    if (filterCat !== "all" && p.categoria !== filterCat) return false;
    if (filterEstado !== "all" && p.estado !== filterEstado) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        p.nombre.toLowerCase().includes(q) ||
        p.descripcion.toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q)) ||
        (p.servicios_aplicables || []).some(s => s.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const kpis = [
    { label: "Apps en producción", val: projects.filter(p => p.estado === "produccion").length, color: "text-green-600", bg: "bg-green-50" },
    { label: "Pilotos activos",    val: projects.filter(p => p.estado === "piloto").length,      color: "text-blue-600",  bg: "bg-blue-50"  },
    { label: "En desarrollo",      val: projects.filter(p => p.estado === "desarrollo").length,  color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Total proyectos",    val: projects.length,                                          color: "text-gray-700",  bg: "bg-gray-50"  },
  ];

  // Descarga autenticada: fetch con Bearer token → blob → trigger descarga
  const downloadAuthPdf = async (path, filename) => {
    try {
      const resp = await axios.get(`${API}${path}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("No se pudo descargar el PDF");
    }
  };

  return (
    <div className="space-y-5 w-full" data-testid="innovacion-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Innovación Digital</h1>
          <p className="text-sm text-gray-500 mt-0.5">Portfolio de aplicaciones digitales y base de conocimiento para técnicos de ofertas</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => downloadAuthPdf("/innovation/fichas-pdf", "Fichas_Innovacion_Digital.pdf")}
            className="flex items-center gap-2 border border-gray-200 text-gray-700 text-xs font-semibold px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors"
            data-testid="download-fichas-pdf-btn"
          >
            <Download size={13} /> Fichas PDF
          </button>
          <button
            type="button"
            onClick={() => downloadAuthPdf("/innovation/manual-ofertas-pdf", "Manual_Ofertas_VSM.pdf")}
            className="flex items-center gap-2 bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-[#006528] transition-colors"
            data-testid="download-manual-pdf-btn"
          >
            <Download size={13} /> Manual PDF
          </button>
          <div className="flex items-center gap-2 bg-[#007934]/5 border border-[#007934]/20 rounded-xl px-3 py-2">
            <Zap size={14} className="text-[#007934]" />
            <span className="text-xs font-semibold text-[#007934]">Contexto RAG activo</span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="innovation-kpis">
          {kpis.map(k => (
            <div key={k.label} className={`rounded-xl border border-gray-100 p-3.5 ${k.bg}`}>
              <p className={`text-2xl font-bold font-['Outfit'] ${k.color}`}>{k.val}</p>
              <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        {[
          { key: "portfolio", label: "Portafolio de Apps", icon: Lightbulb },
          { key: "knowledge", label: "Base de Conocimiento RAG", icon: BookOpen },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
                tab === t.key ? "border-[#007934] text-[#007934]" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`tab-${t.key}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* PORTFOLIO TAB */}
      {tab === "portfolio" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Filtrar proyectos..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                data-testid="project-search"
              />
            </div>

            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] bg-white"
              data-testid="filter-category">
              <option value="all">Todas las categorías</option>
              {categories.map(c => (
                <option key={c.categoria} value={c.categoria}>{c.categoria} ({c.count})</option>
              ))}
            </select>

            <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] bg-white"
              data-testid="filter-estado">
              <option value="all">Todos los estados</option>
              <option value="produccion">En producción</option>
              <option value="piloto">Piloto activo</option>
              <option value="desarrollo">En desarrollo</option>
              <option value="contratado">Contratado</option>
              <option value="concepto">Concepto/PoC</option>
            </select>

            {(filterCat !== "all" || filterEstado !== "all" || searchTerm) && (
              <button onClick={() => { setFilterCat("all"); setFilterEstado("all"); setSearchTerm(""); }}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                <X size={12} /> Limpiar
              </button>
            )}
          </div>

          {/* Category legend */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(CAT_COLORS).map(([cat, color]) => {
              const count = projects.filter(p => p.categoria === cat).length;
              if (!count) return null;
              return (
                <button key={cat} onClick={() => setFilterCat(filterCat === cat ? "all" : cat)}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-all ${
                    filterCat === cat ? "shadow-sm" : "opacity-60 hover:opacity-90"
                  }`}
                  style={{ background: color + "10", borderColor: color + "30", color }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                  {cat} ({count})
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-400 font-medium">{filtered.length} de {projects.length} proyectos</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="projects-grid">
                {filtered.map(p => (
                  <ProjectCard key={p.id} project={p} onClick={setSelectedProject} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* KNOWLEDGE BASE TAB */}
      {tab === "knowledge" && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="bg-gradient-to-r from-[#007934]/5 to-[#007934]/0 border border-[#007934]/10 rounded-xl p-4 flex-1">
              <div className="flex items-start gap-3">
                <Zap size={16} className="text-[#007934] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Base de Conocimiento RAG</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Esta base de conocimiento se usa automáticamente como contexto en el <strong>chatbot de licitaciones</strong>.
                    Cuando el asistente Pando detecta palabras clave (GPS, IoT, ORA, RSU...) consulta aquí para enriquecer sus respuestas
                    con soluciones reales de Valoriza.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 bg-[#007934] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-[#006528] transition-colors flex-shrink-0"
              data-testid="add-rag-doc-btn"
            >
              <Plus size={14} /> Añadir documento
            </button>
          </div>

          {showUpload && (
            <RAGUploadModal
              onClose={() => setShowUpload(false)}
              onSuccess={() => { setShowUpload(false); loadRagDocs(); }}
            />
          )}

          {/* Uploaded documents list */}
          {ragDocs.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText size={12} /> Documentos indexados ({ragDocs.length})
              </h3>
              <div className="space-y-2">
                {ragDocs.map((doc, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={13} className="text-[#007934] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{doc.proyecto || doc.fichero}</p>
                        <p className="text-xs text-gray-400">{doc.fichero} · {doc.chunks} fragmentos · {doc.categoria}</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`¿Eliminar todos los fragmentos de "${doc.fichero}"?`)) return;
                        try {
                          await axios.delete(`${API}/innovation/rag-documents/${encodeURIComponent(doc.fichero)}`);
                          toast.success("Documento eliminado del RAG");
                          loadRagDocs();
                        } catch { toast.error("Error al eliminar"); }
                      }}
                      className="ml-3 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                      data-testid={`delete-rag-doc-${i}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <KnowledgeSearch />

          {/* Quick reference cards */}
          <div className="pt-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Índice rápido por categoría</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(CAT_COLORS).map(([cat, color]) => {
                const catProjects = projects.filter(p => p.categoria === cat);
                if (!catProjects.length) return null;
                return (
                  <div key={cat} className="bg-white border border-gray-100 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                      <span className="text-xs font-semibold text-gray-700">{cat}</span>
                    </div>
                    <div className="space-y-1">
                      {catProjects.map(p => (
                        <button key={p.id} onClick={() => { setSelectedProject(p); }}
                          className="w-full text-left text-xs text-gray-500 hover:text-[#007934] flex items-center gap-1.5 py-0.5 group transition-colors">
                          <ChevronRight size={10} className="flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                          {p.nombre}
                          <span className="ml-auto text-[9px] text-gray-300">{ESTADO_CFG[p.estado]?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Project Detail Modal */}
      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </div>
  );
}
